import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { GridFieldEditorComponent } from '../components/form-fields/grid-field-editor/grid-field-editor.component';
import { AsyncJob } from '../models/async-job.model';

// A general purpose service for communicating across components within the UI
@Injectable()
export class CommunicationService {

    // Pharvey - 6/3/2024 - added for VNEXT-1294 to allow any unsaved changes in grids to be saved to pending before the form as a whole is saved
    private gridComponents: GridFieldEditorComponent[] = [];

    private aFormInstanceIsLoading: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    private aFormInstanceIsLoadingMessage: BehaviorSubject<string> = new BehaviorSubject<string>('');
   
    constructor() { }

    // The "Skip Navigation" link (an accessibility measure) is in the top-level component
    // and in order for it to be kept up to date, lower-level components need to notify
    // that it has changed.
    private skipNavLinkSubject = new Subject<string>();

    // This is what AppComponent subscribes to (see its constructor)
    public skipLinkChanged = this.skipNavLinkSubject.asObservable();

    // Called by components needing to notify that the skip link's url has changed
    // TODO: consider removing this and calls to it -- may not need to do this if skip to can be set
    // during initialization of AppComponent...
    public emitChangeToSkipNavLink(newLink: string, target: string = null) {
        newLink = newLink.split('#')[0]; // strip off anchor if present
        target = target == null ? "#main-content" : target;
        newLink = `${newLink}${target}`;
        this.skipNavLinkSubject.next(newLink);
    }

    public startedLoadingFormInstance(id: number) {
        this.UpdateAsyncFormInstanceLoading(id);
    }

    // PHarvey - 6/3/2024 - for VNEXT-1294
    // I was going to put these methods in KendoGridService BUT that service is scoped
    // to each individual Grid and is not a singleton - I thought it would be confusing to also
    // have a singleton instance of it, so I placed them here in this generic service
    // (Perhaps it should go in a new, dedicated service?)
    public registerGrid(gridFieldEditorComponent: GridFieldEditorComponent) {
        this.gridComponents.push(gridFieldEditorComponent);
    }

    public initializeGridComponents() {
        this.gridComponents = [];
    }

    // Returns a Promise for all registered grids on a given Form. Each one is given the opportunity to save any unsaved rows.
    // The caller of this method can then wait for all of them to complete
    public saveAnyUnsavedGridData(): Promise<any> {
        let promises = [];
        for (let grid of this.gridComponents) {
            promises.push(grid.saveAnyUnsavedRows('communicationService'));
        }
        return Promise.all(promises);
    }

    // PJH - 7/30/2025 - Added for VNEXT-1867 - now that forms are not reloaded after saving
    // we can run into situations where a grid has stale data in it which results in errors
    // upon further edits. So this was added to enable the reloading of registered Grids
    public reloadGrids(): void {
        for (let grid of this.gridComponents) {
            grid.rebind();
        }
    }

    // Added to imporove semantics. The display of the "Form opening" dialog is governed by the value of
    // aFormInstanceIsLoading and if it's null that means any dialog that's been initiated should be closed
    // (See the detailed comment in shared\dialogs\form-instance-operations\operations\open-form.operation.ts)
    public cancelAnyOpenFormDialog(): void {
        this.UpdateAsyncFormInstanceLoading(null);
    }

    public UpdateAsyncFormInstanceLoading(id: number): void {
        if (id) {
            this.ResetAsyncFormInstanceLoadingMessage();
        }
        this.aFormInstanceIsLoading.next(id);
    }

    public ResetAsyncFormInstanceLoadingMessage(): void {
        this.aFormInstanceIsLoadingMessage.next('');
    }
    public UpdateAsyncFormInstanceLoadingMessage(message: string): void {
        this.aFormInstanceIsLoadingMessage.next(message);
    }

    public get AFormInstanceIsLoading(): BehaviorSubject<number> {
        return this.aFormInstanceIsLoading;
    }
    
    public get AFormInstanceIsLoadingMessage(): BehaviorSubject<string> {
        return this.aFormInstanceIsLoadingMessage;
    }

}
